import { Formik, FormikProps } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import InfoOverview from 'Routes/Parameters/components/InfoOverview';
import Header from 'Routes/Parameters/Pages/CreateExpression/Header';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useAddExpressionParameter from 'Hooks/query-hooks/parameters/useAddExpressionParameter';
import useIsLeavingForm from 'Hooks/useIsLeavingForm';
import { callAll, generateParameterSystemName, getEmptyStringReplacementText } from 'Utils';
import { addParameterValidationSchema } from 'Components/CreateExpressionForm/createExpressionSchema';
import ScriptOverview from 'Components/ScriptOverview';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import { Label } from '@brighterion/br-ui';
import { CREATE_EXPRESSION_PARAM_INPUTS } from 'Common/parameterEnums';
import { FormikInputField } from 'Components/FormikComponents';
import { HandleChange, HandleBlur, SetFieldValue } from 'Components/MerchantOverviewModal/types';
import { TFunction } from 'i18next';
import styles from './CreateExpressionForm.css';

type CreateExpressionFormProps = {
    initialValues: ParameterExpressionFormSchema;
    parameterData?: UseGetParametersResponse | undefined;
};

export const genParameterInfoOverviewTop = (
    t: TFunction,
    isSubmitting: boolean,
    values: ParameterExpressionFormSchema,
    handleChange: HandleChange,
    handleBlur: HandleBlur,
    setFieldValue?: SetFieldValue
) => {
    const generatedParameterSystemName = generateParameterSystemName(values.displayName);

    return [
        {
            id: CREATE_EXPRESSION_PARAM_INPUTS.DISPLAY_NAME,
            label: t('Display Name'),
            data: (
                <FormikInputField
                    label={t('Display Name')}
                    name={CREATE_EXPRESSION_PARAM_INPUTS.DISPLAY_NAME}
                    className={styles.nameField}
                    // on change, reset the editor value
                    handleChange={callAll(handleChange, () => {
                        // If user modifies this field after script is validated
                        // force them to revalidate the script
                        if (values.isScriptValidated) {
                            setFieldValue?.('revalidateScript', true);
                            setFieldValue?.('isScriptValidated', false);
                        }
                    })}
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    optionalMsg={t('addValidParameterDisplayName')}
                />
            )
        },
        {
            id: CREATE_EXPRESSION_PARAM_INPUTS.SYSTEM_NAME,
            label: t('System Name'),
            data: (
                <FormikInputField
                    label={t('System Name')}
                    name={CREATE_EXPRESSION_PARAM_INPUTS.SYSTEM_NAME}
                    className={styles.nameField}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    // overrideValue is being used to display formatted display name in the screen
                    // it does not set the field value
                    overrideValue={generatedParameterSystemName}
                    disabled
                    optionalMsg={t('parameterSystemName')}
                />
            )
        },
        {
            id: CREATE_EXPRESSION_PARAM_INPUTS.TPP_TYPE,
            label: t('Output'),
            data: (
                <div className={styles.outputText}>
                    <Label label={t('Output')} id={CREATE_EXPRESSION_PARAM_INPUTS.TPP_TYPE} />
                    <span>
                        {t('Type')}: {getEmptyStringReplacementText(values?.tppType)}
                    </span>
                </div>
            )
        }
    ];
};

export const genParameterInfoOverviewBottom = (
    t: TFunction,
    isSubmitting: boolean,
    handleChange: HandleChange,
    handleBlur: HandleBlur
) => [
    {
        id: CREATE_EXPRESSION_PARAM_INPUTS.DESCRIPTION,
        label: t('Parameter Description'),
        data: (
            <FormikInputField
                label={t('Parameter Description')}
                name={CREATE_EXPRESSION_PARAM_INPUTS.DESCRIPTION}
                className={styles.descriptionField}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={isSubmitting}
            />
        )
    }
];

const CreateExpressionForm: React.FC<CreateExpressionFormProps> = ({ initialValues, parameterData }) => {
    const formRef = React.useRef<FormikProps<ParameterExpressionFormSchema>>(null);
    useIsLeavingForm(formRef);
    const { t } = useTranslation();
    const { push } = useHistory();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();

    const onSuccess = async (_data: any, variables: CreateExpressionParameterRequest[]) => {
        addAlert({
            id: 'add-expression-parameter',
            content: t('CreateExpressionParameterSuccessModalText', {
                displayName: variables[0].message,
                status: variables[0].status
            }),
            status: 'success'
        });
        push(ROUTES.PARAMETERS.INDEX);
    };

    const onError = (_err: any, variables: CreateExpressionParameterRequest[]) => {
        addModal({
            id: 'add-expression-parameter-error',
            status: 'error',
            title: t('Parameter could not be saved'),
            content: t('CreateExpressionParameterErrorModalText', { displayName: variables[0].fieldName }),
            cancelText: t('Close')
        });
        // retain user's values
        formRef.current?.resetForm({
            values: {
                ...formRef.current.values,
                isScriptValidated: false,
                revalidateScript: true
            },
            touched: formRef.current.touched,
            errors: formRef.current.errors,
            isSubmitting: false
        });
    };

    const mutationOptions = { onSuccess, onError };
    const mutation = useAddExpressionParameter(mutationOptions);

    const handleFormSubmit = (formValues: ParameterExpressionFormSchema) => {
        const formattedSystemName = generateParameterSystemName(formValues.displayName);
        const { description, displayName, expression, tppType, status } = formValues;

        const payload: CreateExpressionParameterRequest[] = [
            {
                description,
                expressionStatement: expression.expText,
                fieldName: formattedSystemName,
                message: displayName,
                status,
                tppType
            }
        ];

        mutation.mutate(payload);
    };

    const handleCancel = () => {
        push(ROUTES.PARAMETERS.INDEX);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            validationSchema={addParameterValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { isSubmitting, handleChange, handleBlur, values, setFieldValue } = formikProps;
                const paramInfoOverviewTopData = genParameterInfoOverviewTop(
                    t,
                    isSubmitting,
                    values,
                    handleChange,
                    handleBlur,
                    setFieldValue
                );
                const paramInfoOverviewBottomData = genParameterInfoOverviewBottom(
                    t,
                    isSubmitting,
                    handleChange,
                    handleBlur
                );

                return (
                    <>
                        <Header handleCancel={handleCancel} mode='create' />
                        <InfoOverview
                            dataTestId='parameterInformation'
                            leftTitle={t('Parameter Information')}
                            topData={paramInfoOverviewTopData}
                            bottomData={paramInfoOverviewBottomData}
                        />

                        <ScriptOverview
                            scriptType='parameter'
                            inputFields={[]}
                            leftTitle={t('Parameter Statement')}
                            parameterData={parameterData}
                            readOnly={false}
                        />
                    </>
                );
            }}
        </Formik>
    );
};

export default CreateExpressionForm;
